// Money-Summen für den "Zahlungsverkehr"-Block (Offene/Bezahlte/Summe aller Posten).
//
// openAmount-Konvention (siehe swissQR/ImportHistory/allocationState.ts):
//   null/undefined ⟺ "voller paymentAmount offen"   ·   0 ⟺ Rate ist vollständig bezahlt.
//
// Deshalb darf `openAmount ?? paymentAmount` nur für OFFENE Posten verwendet werden.
// Für BEZAHLTE Posten liefert diese Formel 0 (weil `0 ?? paymentAmount === 0`, Nullish-
// Coalescing greift nicht bei 0) — genau das ließ bei "außerhalb Mollie bezahlt"-Posten
// alle drei Kennzahlen auf 0 € fallen. Bezahlte Posten zählen daher mit `paymentAmount`.
//
// "Summe aller Posten" ist die Summe ALLER paymentAmount (= booking.grossPrice, Buchungspreis).
// "Bezahlte Posten" zählt zusätzlich den schon bezahlten Teil teilbezahlter offener Raten
// (paymentAmount − openAmount, B-82276: 51 € von 56 €), damit offen + bezahlt = Summe.
// Dieser Teil steht als `partialPaidAmount` separat bereit (Fußnote unter der bezahlten Liste,
// weil er dort keine eigene Zeile hat).

import {roundToCents} from "../../../shared/money";

type PaymentForTotals = {
    paymentDone?: string | null;
    openAmount?: number | null;
    paymentAmount?: number | null;
};

export const calcPaymentTotals = (payments: readonly PaymentForTotals[] = []) => {
    // Offen: echter Teilrest (0 < openAmount < paymentAmount), sonst der volle Betrag. Ein
    // openAmount von 0 ohne paymentDone ist ein Drift-Zustand und zählt als voll offen.
    const openAmount = payments.reduce((acc, p) => {
        if (p.paymentDone != null) {
            return acc;
        }
        const amount = p.paymentAmount ?? 0;
        const open = p.openAmount;

        return acc + (open != null && open > 0 && open < amount ? open : amount);
    }, 0);
    const partialPaidAmount = payments.reduce((acc, p) => {
        const amount = p.paymentAmount ?? 0;
        const open = p.openAmount;

        return p.paymentDone == null && open != null && open > 0 && open < amount ? acc + (amount - open) : acc;
    }, 0);
    const doneAmount = payments.reduce((acc, p) => (p.paymentDone != null ? acc + (p.paymentAmount ?? 0) : acc), 0) + partialPaidAmount;

    const sumAmount = payments.reduce((acc, p) => acc + (p.paymentAmount ?? 0), 0);

    return {openAmount, doneAmount, sumAmount, partialPaidAmount};
};

export type CancelSplitPost = PaymentForTotals & {keptOnCancelAt?: unknown};

/**
 * Stornierte Buchung (ADR 0022): offene Posten ohne Merker `keptOnCancelAt` sind erlassen. Sie zählen nicht mehr in
 * „Summe aller Posten“, sondern stehen als eigener Block „Erlassene Posten“ (Testlauf 30.10.2026, Befunde 17/20).
 * Erlassen ist nur der offene Rest: der schon bezahlte Teil einer teilbezahlten Rate (`waivedPaidAmount`) bleibt
 * eingegangenes Geld und gehört weiter zu „Bezahlte Posten“ und „Summe aller Posten“ (Review #1797).
 */
export const splitCancelledPosts = <T extends CancelSplitPost>(
    posts: readonly T[],
    isCanceled: boolean,
): {counted: T[]; waived: T[]; waivedAmount: number; waivedPaidAmount: number} => {
    if (!isCanceled) {
        return {counted: [...posts], waived: [], waivedAmount: 0, waivedPaidAmount: 0};
    }
    // Ohne Bezahldatum und ohne Merker ist ein Posten erlassen, auch im Drift-Zustand openAmount 0
    const waived = posts.filter((p) => p.paymentDone == null && !p.keptOnCancelAt);
    const counted = posts.filter((p) => waived.indexOf(p) === -1);
    const waivedAmount = roundToCents(waived.reduce((sum, p) => sum + Number(p.openAmount ?? p.paymentAmount), 0));
    const waivedPaidAmount = roundToCents(calcPaymentTotals(waived).partialPaidAmount);

    return {counted, waived, waivedAmount, waivedPaidAmount};
};
